import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useCerberus, RequiredAction } from "../../hooks/useCerberus";
import RequiredActionsDialog from "./RequiredActionsDialog";
import WelcomeSurveyDialog from "./WelcomeSurveyDialog";
import { useBranding } from "../../branding/BrandingProvider";
import { useModuleEnabled } from "../../modules/ModulesProvider";
import { getRunConfig } from "../../config/runtimeConfig";
import { TH_QUESTIONNAIRE_ID } from "../../modules/headache-questionnaire/constants";

const RequiredActionsManager: React.FC = () => {
  const brand = useBranding();
  const navigate = useNavigate();
  const headacheQuestionnaireEnabled = useModuleEnabled("headache-questionnaire");
  const { getCriticalActions, loading, completeRequiredAction } = useCerberus();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<RequiredAction | null>(
    null,
  );
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    // Only check for required actions after user data has loaded
    if (!loading) {
      const criticalActions = getCriticalActions();
      if (criticalActions.length > 0) {
        setCurrentAction(criticalActions[0]);
        setDialogOpen(true);
      }
    }
  }, [loading, getCriticalActions]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentAction(null);
  };

  const handleWelcomeComplete = async (
    completionData: Record<string, unknown>,
  ) => {
    if (!currentAction) return;

    const success = await completeRequiredAction(
      currentAction.id,
      currentAction.version,
      completionData,
    );

    if (success) {
      // Check if there are more critical actions
      const remainingActions = getCriticalActions();
      if (remainingActions.length > 1) {
        // Show next action
        setCurrentAction(remainingActions[1]);
      } else {
        // No more critical actions, close dialog
        handleDialogClose();
      }
    }
  };

  const handleGoToQuestionnaire = () => {
    // Close the welcome dialog immediately
    handleDialogClose();

    // The headache questionnaire is defined in CODE (ThFormRegistrar.FormSetId on the API), not
    // as a database row, so its id is the same on every cell — and the API's /questionnaire/th
    // routes refuse any other. This was made config-only on the belief that it differed per cell,
    // and since no TH cell set it, every new patient's hand-off ended in "isn't available yet"
    // instead of the questionnaire. The config value remains an override.
    const questionnaireId = getRunConfig()?.welcomeSurveyQuestionnaireId || TH_QUESTIONNAIRE_ID;

    // Set navigating state to show loading overlay
    setIsNavigating(true);

    navigate(`/questionnaire/${questionnaireId}`);

    // Extended timeout for testing - 9 seconds to verify smooth transition
    setTimeout(() => {
      setIsNavigating(false);
    }, 5000);
  };

  // The Welcome Survey is TeleHeadache clinical content — headache medications, dosing, and a
  // headache assessment hand-off. It used to be selected on the action's NAME alone, with no
  // brand or module gate, so a memory-care patient handed a "Welcome Survey" action was walked
  // through a headache intake. Gated on the module that owns that content instead; when it is
  // off, the action falls through to the generic required-actions dialog.
  const isWelcomeSurvey =
    currentAction?.actionName === "Welcome Survey" && headacheQuestionnaireEnabled;

  return (
    <>
      {isWelcomeSurvey ? (
        <WelcomeSurveyDialog
          open={dialogOpen}
          action={currentAction}
          onComplete={handleWelcomeComplete}
          onGoToQuestionnaire={handleGoToQuestionnaire}
          isNavigating={isNavigating}
        />
      ) : (
        <RequiredActionsDialog open={dialogOpen} onClose={handleDialogClose} />
      )}

      {/* Loading overlay during navigation */}
      <Dialog
        open={isNavigating}
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            background: (theme) => theme.palette.brand.gradient.hero,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            color: "brand.onHero",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <img
              src={brand.logoWhite}
              alt={`${brand.displayName} logo`}
              style={{
                height: "60px",
                objectFit: "contain",
              }}
            />
          </Box>
          <CircularProgress
            size={60}
            sx={{
              color: "brand.onHero",
              mb: 3,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 1,
            }}
          >
            Loading Questionnaire...
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.9,
              maxWidth: "400px",
            }}
          >
            Preparing your personalized headache assessment
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequiredActionsManager;
